import type { MenuProps } from "antd";
import { Badge, Dropdown, Tooltip } from "antd";
import { ColorAvatar } from "components/ColorAvatar";
import type { IFlagsmithFeature } from "flagsmith/react";
import { useFlags } from "flagsmith/react";
import { useMyTicketsAwaitingReply } from "hooks/useSupportTickets";
import { useUser } from "hooks/useUser";
import { ChevronUp, Headphones, LogOut, User } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

/**
 * The user button at the bottom of the sidebar, and the menu it opens upward
 * (EQU-1120).
 *
 * Account, Support and Log out used to be three nav rows of their own. They
 * are the same three actions, pointed at the same places; what changed is that
 * they now live behind the person they belong to instead of competing with the
 * tools above them for attention.
 */
export function SidebarUserButton({ railed }: { railed: boolean }) {
	const { user, logout } = useUser();
	const navigate = useNavigate();
	const [open, setOpen] = useState(false);
	const flags = useFlags(["disable_support_ticket"]);
	const isSupportDisabled = !!(
		flags.disable_support_ticket as IFlagsmithFeature
	)?.enabled;
	// Tickets support has replied to and this user has not answered. Skipped
	// when the Support row itself is flagged off — nothing would render it.
	const myTicketsAwaitingReply = useMyTicketsAwaitingReply(
		!!user && !isSupportDisabled,
	);

	if (!user) return null;

	// Trimmed: a user with neither name set interpolates to a single space,
	// which is truthy, so a `|| "User"` fallback would never fire (EQU-799 §1
	// hit exactly this in the header).
	const fullName = `${user.firstName} ${user.lastName}`.trim() || "User";
	const teamName = user.member?.team?.name;

	const items: MenuProps["items"] = [
		{
			key: "header",
			type: "group",
			label: (
				<div className="sider-user-menu-header">
					<div className="sider-user-menu-header-name">{fullName}</div>
					<div className="sider-user-menu-header-email">{user.email}</div>
				</div>
			),
		},
		{
			key: "account",
			icon: <User size={16} strokeWidth={1.8} />,
			label: "Account",
			onClick: () => navigate("/account/billing"),
		},
		...(isSupportDisabled
			? []
			: [
					{
						key: "support",
						icon: <Headphones size={16} strokeWidth={1.8} />,
						label: (
							<span>
								Support
								<Badge
									count={myTicketsAwaitingReply}
									size="small"
									style={{ marginLeft: 8 }}
								/>
							</span>
						),
						onClick: () => navigate("/support"),
					},
				]),
		{ type: "divider" as const },
		{
			key: "signout",
			icon: <LogOut size={16} strokeWidth={1.8} />,
			label: "Log out",
			danger: true,
			onClick: () => logout(),
		},
	];

	const button = (
		<button
			type="button"
			className={`sider-user-button${railed ? " sider-user-button--railed" : ""}`}
			aria-haspopup="menu"
			aria-expanded={open}
			aria-label={railed ? `${fullName}, account menu` : undefined}
		>
			<ColorAvatar size={32} name={fullName} src={user.photoUrl} />
			{!railed && (
				<>
					<span style={{ minWidth: 0, flex: 1 }}>
						<span className="sider-user-name">{fullName}</span>
						{teamName && <span className="sider-user-team">{teamName}</span>}
					</span>
					<ChevronUp
						size={16}
						strokeWidth={1.8}
						className="sider-user-chevron"
						aria-hidden="true"
					/>
				</>
			)}
		</button>
	);

	return (
		<div className="sider-user-block">
			<Dropdown
				menu={{ items }}
				trigger={["click"]}
				// Upward, over the sidebar: the button is the bottom-most control
				// in the viewport, so a downward menu would open off-screen.
				placement="topLeft"
				overlayClassName="sider-user-menu"
				open={open}
				onOpenChange={setOpen}
			>
				{railed ? (
					<Tooltip title={fullName} placement="right">
						{button}
					</Tooltip>
				) : (
					button
				)}
			</Dropdown>
		</div>
	);
}
